<script setup lang="ts">
/**
 * AiBuddy · 右下角悬浮 AI 助手（AI 角色 + 技巧/鸡汤气泡，可拖拽）
 * ------------------------------------------------------------
 * - 形象 = AiCharacter（float 形制）：与顶栏同一个「状态小剧场」，随在线状态
 *   换场景（窗台/工位/走廊/夜窗……），不再是旧的机器人卡通；
 * - 原机器人能力迁移保留：系统小技巧（含快捷键）+ 心灵鸡汤，素材在
 *   aichar/tips.ts，7s 轮播，点击角色立即换一条；
 * - 可整体拖拽、位置记忆；恢复与窗口 resize 时都把位置夹回视口内
 *   （修复：旧存档坐标超出当前视口时面板渲染在屏外，「点了按钮不显示」）；
 * - 显隐由 useAiBuddy() 控制（工作条 AI 按钮 toggle）。
 */
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useAiBuddy } from './aibuddy'
import AiCharacter from './aichar/AiCharacter.vue'
import { useAiActivity } from './aichar/activity'
import { TIPS } from './aichar/tips'

const buddy = useAiBuddy()
/* AI 回答实时活动：生成期间气泡改播小人念叨（角标「在忙」），暂停技巧轮播 */
const { current: aiAct, line: actLine } = useAiActivity()

/* ---------- 气泡轮播：小技巧（含快捷键）+ 心灵鸡汤 ---------- */
const idx = ref(Math.floor(Math.random() * TIPS.length))
const cur = () => TIPS[idx.value % TIPS.length]
const popTick = ref(0)
let timer: ReturnType<typeof setInterval> | null = null
function next() { idx.value = (idx.value + 1) % TIPS.length; popTick.value++ }
function startTimer() { stopTimer(); timer = setInterval(next, 7000) }
function stopTimer() { if (timer) { clearInterval(timer); timer = null } }

/* ---------- 位置：默认右下角；可拖拽并记忆；恢复/resize 均夹回视口 ---------- */
const KEY = 'ema.ai.buddy.v1'
const pos = reactive<{ x: number | null; y: number | null }>({ x: null, y: null })
const el = ref<HTMLElement | null>(null)
let drag: { sx: number; sy: number; ox: number; oy: number } | null = null

/* 容器可视尺寸（隐藏时 rect 为 0，回落估值） */
function floatSize() {
  const r = el.value?.getBoundingClientRect()
  return { w: r && r.width > 0 ? r.width : 232, h: r && r.height > 0 ? r.height : 190 }
}
/* 把坐标夹进当前视口（留 6px 边距）：越界存档在此被拉回，面板永远可见 */
function clampPos() {
  if (pos.x === null || pos.y === null) return
  const { w, h } = floatSize()
  pos.x = Math.min(Math.max(6, pos.x), Math.max(6, window.innerWidth - w - 6))
  pos.y = Math.min(Math.max(6, pos.y), Math.max(6, window.innerHeight - h - 6))
}
function savePos() {
  try { localStorage.setItem(KEY, JSON.stringify({ x: pos.x, y: pos.y })) } catch { /* 忽略配额 */ }
}
function onDown(e: PointerEvent) {
  const r = (e.currentTarget as HTMLElement).closest('.ab-float')?.getBoundingClientRect()
  if (!r) return
  pos.x = r.left; pos.y = r.top
  drag = { sx: e.clientX, sy: e.clientY, ox: r.left, oy: r.top }
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
}
function onMove(e: PointerEvent) {
  if (!drag) return
  const { w, h } = floatSize()
  pos.x = Math.min(Math.max(6, drag.ox + e.clientX - drag.sx), window.innerWidth - w - 6)
  pos.y = Math.min(Math.max(6, drag.oy + e.clientY - drag.sy), window.innerHeight - h - 6)
}
function onUp() {
  if (!drag) return
  drag = null
  savePos()
}
function onResize() { clampPos() }

onMounted(() => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') { pos.x = saved.x; pos.y = saved.y }
  } catch { /* 脏数据走默认位置 */ }
  clampPos()
  window.addEventListener('resize', onResize)
  if (buddy.open.value) startTimer()
})
onBeforeUnmount(() => {
  stopTimer()
  window.removeEventListener('resize', onResize)
})

/* 显隐联动轮播：打开即弹一条，隐藏停表；回答活动期不启动轮播（气泡让给念叨） */
watch(buddy.open, v => { if (v) { if (!aiAct.value) { next(); startTimer() } } else stopTimer() })
/* 回答活动：在忙时停轮播；结束后换一条新技巧并恢复轮播 */
watch(aiAct, v => {
  if (!buddy.open.value) return
  if (v) stopTimer()
  else { next(); startTimer() }
})
/* 活动期点击角色只蹦跳不换条（念叨保持）；空闲才换下一条 */
function onPoke() { if (!aiAct.value) next() }
</script>

<template>
  <div v-show="buddy.open.value" ref="el" class="ab-float"
       :style="pos.x !== null && pos.y !== null ? { left: pos.x + 'px', top: pos.y + 'px', right: 'auto', bottom: 'auto' } : {}">
    <!-- 气泡：小技巧（含快捷键）+ 充电鸡汤（原机器人能力迁移）；AI 回答活动期改播小人念叨 -->
    <div :key="aiAct ? 'act:' + actLine : popTick" class="ab-pop" :class="aiAct ? 'act' : cur().kind">
      <button class="ab-x" title="收起助手" @click="buddy.hide()">×</button>
      <i class="ab-kind">{{ aiAct ? '在忙' : cur().kind === 'tip' ? '小技巧' : '充充电' }}</i>
      <p>{{ aiAct ? actLine : cur().text }}</p>
    </div>
    <!-- AI 角色（状态小剧场 · float）：拖拽手柄；点击蹦一下并换一条 -->
    <AiCharacter mode="float" @poke="onPoke"
                 @pointerdown="onDown" @pointermove="onMove" @pointerup="onUp" @pointercancel="onUp" />
  </div>
</template>

<style scoped>
.ab-float {
  position: fixed; right: 26px; bottom: 92px; z-index: 2400;
  width: 232px; display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
  pointer-events: none;
}
.ab-float > * { pointer-events: auto; }

/* 气泡 */
.ab-pop {
  position: relative; width: 100%;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 13px 13px 4px 13px; box-shadow: var(--sh-3);
  padding: 9px 26px 9px 11px;
  animation: abPop .28s cubic-bezier(.2, .9, .3, 1.2);
}
@keyframes abPop { from { opacity: 0; transform: translateY(7px) scale(.92); } }
.ab-pop.mood { border-color: color-mix(in srgb, var(--violet) 32%, transparent); }
/* 在忙（回答活动）：边框带一点主色，提示正在干活 */
.ab-pop.act { border-color: color-mix(in srgb, var(--accent) 38%, transparent); }
.ab-kind {
  position: absolute; top: -9px; left: 10px; font-style: normal;
  font-size: 9px; font-weight: 700; letter-spacing: .06em;
  padding: 1px 7px; border-radius: 99px; color: #fff;
  background: linear-gradient(120deg, var(--accent), var(--violet));
}
.ab-pop.mood .ab-kind { background: var(--violet); }
.ab-pop p { margin: 3px 0 0; font-size: 12px; line-height: 1.65; color: var(--ink-2); }
.ab-pop p::after { content: ''; display: inline-block; width: 1.5px; height: 12px; margin-left: 2px; vertical-align: -2px; background: var(--accent); animation: abBlink 1s steps(1) infinite; }
@keyframes abBlink { 50% { opacity: 0; } }
.ab-x {
  position: absolute; top: 5px; right: 5px; width: 16px; height: 16px;
  border-radius: 5px; display: grid; place-items: center;
  font-size: 12px; line-height: 1; color: var(--ink-4);
}
.ab-x:hover { background: var(--surface-3); color: var(--ink); }

/* 形象由 AiCharacter（float）渲染，这里只补一层悬浮投影 */
.ab-float :deep(.ai-char) { filter: drop-shadow(0 10px 18px rgba(76, 125, 240, .35)); }
</style>
